import axios from "axios";
import { API_BASE_URL, getAuthToken } from "./shared";

// API helpers for Artist Studio > Fan Funding.
const url = (path) => `${API_BASE_URL}/v1/fan-funding/${path}`;
const config = () => ({ headers: { token: getAuthToken(), "Content-Type": "application/json" }, timeout: 20000 });
const result = (response) => response.data?.result;

export const fanFundingApi = {
  get: () => axios.get(url("page"), config()).then(result),
  save: (body) => axios.post(url("page"), body, config()).then(result),
  // { search, page, limit }; the server matches part of the supporter's username or message.
  funding: (params = {}) => axios.get(url("funding"), { ...config(), params }).then(result),
  fundingDetail: (id) => axios.get(url(`funding/${encodeURIComponent(id)}`), config()).then(result),
  // Reports the message on a tip (FSD 6.7.2). body: { reason? }. The message is hidden from the artist while it is reviewed.
  reportTip: (id, body = {}) => axios.post(url(`funding/${encodeURIComponent(id)}/report`), body, config()).then(result),
  // { page, limit }; read-only, newest subscriber first.
  subscribers: (params = {}) => axios.get(url("subscribers"), { ...config(), params }).then(result),
};

// "Sep 17, 2026" and "Sep 17, 2026, 6:42 PM"; a missing or invalid date shows a dash.
export function formatFanDate(value, withTime = false) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  const opts = withTime ? { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" } : { year: "numeric", month: "short", day: "numeric" };
  return d.toLocaleString("en-US", opts);
}

// "Sep 07, 2026" (MMM DD, YYYY, with a two-digit day); a missing or invalid date shows a dash.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatJoinedDate(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}, ${d.getFullYear()}`;
}

// What the Message column shows. A reported message stays hidden while it is under review and after the report is upheld.
export const MESSAGE_STATE_TEXT = { under_review: "Message hidden while we review your report", removed: "Message removed" };
export const REPORT_REASON_MAX = 300;

// Supporter usernames are shown as @handle.
export const supporterHandle = (name) => {
  const handle = String(name || "").trim().replace(/^@+/, "");
  return handle ? `@${handle}` : "—";
};

const DEFAULT_RULES = {
  bio_max_length: 1000,
  cover: { extensions: ["jpg", "jpeg", "png", "webp"], max_bytes: 5 * 1024 * 1024, min_width: 600, min_height: 200, max_width: 6000, max_height: 6000, recommended_width: 1500, recommended_height: 500 },
};

// The bio is plain text of limited length. Empty is allowed (the page simply shows no bio).
export function validateBio(value, rules = DEFAULT_RULES) {
  const text = String(value ?? "").trim();
  if (text.length > rules.bio_max_length) return `Bio must be at most ${rules.bio_max_length} characters.`;
  if (/[<>\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(text)) return "Bio cannot contain < or > or other unsupported characters.";
  return "";
}

const mb = (bytes) => `${Math.round((bytes / 1024 / 1024) * 10) / 10} MB`;

// File type and size are checked before the image is read; dimensions are checked once they are known.
export function checkCoverFile(file, rules = DEFAULT_RULES) {
  const c = rules.cover;
  const ext = String(file?.name || "").split(".").pop().toLowerCase();
  if (!c.extensions.includes(ext)) return "The cover image must be a JPG, PNG or WebP file.";
  if (!file.size) return "The file is empty.";
  if (file.size > c.max_bytes) return `The cover image is too large. Please use a file up to ${mb(c.max_bytes)}.`;
  return "";
}

export function checkCoverSize(width, height, rules = DEFAULT_RULES) {
  const c = rules.cover;
  if (!width || !height) return "This file could not be read as an image.";
  if (width < c.min_width || height < c.min_height) {
    return `The cover image is too small. Use at least ${c.min_width} × ${c.min_height} pixels (recommended ${c.recommended_width} × ${c.recommended_height}).`;
  }
  if (width > c.max_width || height > c.max_height) return `The cover image is too large. Use at most ${c.max_width} × ${c.max_height} pixels.`;
  return "";
}

// A valid image that is not 1500 × 500 is accepted, with guidance instead of an error.
export function coverGuidance(width, height, rules = DEFAULT_RULES) {
  const c = rules.cover;
  if (width === c.recommended_width && height === c.recommended_height) return "";
  return `This image is ${width} × ${height} pixels. For the best result use ${c.recommended_width} × ${c.recommended_height}; other sizes may be cropped on your Fan Page.`;
}

// Resolves { width, height } for an image file, or rejects when the file is not a readable image.
export function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight, previewUrl: objectUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("This file could not be read as an image."));
    };
    img.src = objectUrl;
  });
}

export { DEFAULT_RULES as DEFAULT_FAN_RULES };
